import { MaterialCommunityIcons } from "@expo/vector-icons";
import { AttachmentFile, FileCategory, FileTypeConfig, ValidationError } from "../types";

// File type configurations with their supported formats
export const FILE_TYPE_CONFIGS: Record<FileCategory, FileTypeConfig> = {
  image: {
    category: "image",
    extensions: [".jpg", ".jpeg", ".png", ".gif", ".webp", ".svg", ".bmp", ".ico"],
    mimeTypes: ["image/*"],
    maxSize: 10 * 1024 * 1024, // 10MB
    icon: "image",
    color: "#4CAF50",
  },
  document: {
    category: "document",
    extensions: [".pdf", ".doc", ".docx", ".txt", ".csv", ".xls", ".xlsx", ".ppt", ".pptx", ".rtf"],
    mimeTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    ],
    maxSize: 50 * 1024 * 1024, // 50MB
    icon: "file-document",
    color: "#FF9800",
  },
  video: {
    category: "video",
    extensions: [".mp4", ".mov", ".avi", ".webm", ".mkv", ".flv", ".wmv"],
    mimeTypes: ["video/*"],
    maxSize: 100 * 1024 * 1024, // 100MB
    icon: "video",
    color: "#E91E63",
  },
  audio: {
    category: "audio",
    extensions: [".mp3", ".wav", ".aac", ".m4a", ".ogg", ".flac"],
    mimeTypes: ["audio/*"],
    maxSize: 25 * 1024 * 1024, // 25MB
    icon: "music",
    color: "#9C27B0",
  },
  other: {
    category: "other",
    extensions: [".zip", ".rar", ".7z", ".tar", ".gz"],
    mimeTypes: [
      "application/zip",
      "application/x-rar-compressed",
      "application/x-7z-compressed",
      "application/x-tar",
      "application/gzip",
    ],
    maxSize: 100 * 1024 * 1024, // 100MB
    icon: "file",
    color: "#607D8B",
  },
};

/**
 * Determines the file category based on file extension or MIME type
 */
export function getFileCategory(fileName: string, mimeType?: string): FileCategory {
  const extension = getFileExtension(fileName);

  // Check by MIME type first if available
  if (mimeType) {
    for (const [category, config] of Object.entries(FILE_TYPE_CONFIGS)) {
      if (
        config.mimeTypes.some((type) => {
          if (type.endsWith("/*")) {
            return mimeType.startsWith(type.slice(0, -2));
          }
          return mimeType === type;
        })
      ) {
        return category as FileCategory;
      }
    }
  }

  // Fall back to extension check
  for (const [category, config] of Object.entries(FILE_TYPE_CONFIGS)) {
    if (config.extensions.includes(extension)) {
      return category as FileCategory;
    }
  }

  return "other";
}

/**
 * Gets the file extension from a filename
 */
export function getFileExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot !== -1 ? fileName.substring(lastDot).toLowerCase() : "";
}

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Validates a file against the given restrictions
 */
export function validateFile(
  file: File | { name: string; size: number; type: string },
  options: {
    maxFileSize?: number;
    acceptedTypes?: string[];
    existingFiles?: AttachmentFile[];
  } = {},
): ValidationError | null {
  const { maxFileSize, acceptedTypes, existingFiles = [] } = options;

  // Check file size
  if (maxFileSize && file.size > maxFileSize) {
    return {
      type: "size",
      message: `File size (${formatFileSize(file.size)}) exceeds maximum allowed size (${formatFileSize(maxFileSize)})`,
      file: file.name,
    };
  }

  // Check file type
  if (acceptedTypes && acceptedTypes.length > 0) {
    const extension = getFileExtension(file.name);
    const isValidType = acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return type.toLowerCase() === extension;
      }
      if (type.endsWith("/*")) {
        return file.type.startsWith(type.slice(0, -2));
      }
      return file.type === type;
    });

    if (!isValidType) {
      return {
        type: "type",
        message: `File type "${extension || file.type}" is not allowed`,
        file: file.name,
      };
    }
  }

  // Check for duplicates
  const isDuplicate = existingFiles.some(
    (existingFile) => existingFile.name === file.name && existingFile.size === file.size,
  );

  if (isDuplicate) {
    return {
      type: "duplicate",
      message: `File "${file.name}" already exists`,
      file: file.name,
    };
  }

  return null;
}

/**
 * Validates multiple files
 */
export function validateFiles(
  files: File[],
  options: {
    maxFiles?: number;
    maxFileSize?: number;
    acceptedTypes?: string[];
    existingFiles?: AttachmentFile[];
  } = {},
): ValidationError[] {
  const { maxFiles, existingFiles = [] } = options;
  const errors: ValidationError[] = [];

  // Check total file count
  if (maxFiles && files.length + existingFiles.length > maxFiles) {
    errors.push({
      type: "count",
      message: `Cannot upload ${files.length} files. Maximum allowed is ${maxFiles} (${existingFiles.length} already uploaded)`,
    });
    return errors;
  }

  // Validate each file
  files.forEach((file) => {
    const error = validateFile(file, options);
    if (error) {
      errors.push(error);
    }
  });

  return errors;
}

/**
 * Converts a File object to AttachmentFile
 */
export function fileToAttachmentFile(file: File): AttachmentFile {
  const category = getFileCategory(file.name, file.type);

  return {
    id: crypto.randomUUID(),
    name: file.name,
    size: file.size,
    type: file.type,
    uri: URL.createObjectURL(file),
    category,
    lastModified: file.lastModified,
    file, // Keep reference to original File object for web
    uploadStatus: "pending",
  };
}

/**
 * Gets the appropriate icon for a file based on its category and extension
 */
export function getFileIcon(file: AttachmentFile): keyof typeof MaterialCommunityIcons.glyphMap {
  const config = FILE_TYPE_CONFIGS[file.category];

  // Special cases for specific file types
  const extension = getFileExtension(file.name);
  switch (extension) {
    case ".pdf":
      return "file-pdf-box";
    case ".doc":
    case ".docx":
      return "file-word-box";
    case ".xls":
    case ".xlsx":
      return "file-excel-box";
    case ".ppt":
    case ".pptx":
      return "file-powerpoint-box";
    case ".zip":
    case ".rar":
    case ".7z":
      return "zip-box";
    default:
      return config.icon as keyof typeof MaterialCommunityIcons.glyphMap;
  }
}

/**
 * Gets the color for a file based on its category
 */
export function getFileColor(file: AttachmentFile): string {
  return FILE_TYPE_CONFIGS[file.category].color;
}

/**
 * Creates a thumbnail URL for images
 */
export function createImageThumbnail(file: File, maxSize: number = 200): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("File is not an image"));
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Calculate thumbnail dimensions
      const scale = Math.min(maxSize / img.width, maxSize / img.height);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;

      // Draw resized image
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Convert to blob and create URL
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(URL.createObjectURL(blob));
          } else {
            reject(new Error("Failed to create thumbnail"));
          }
        },
        "image/jpeg",
        0.8,
      );
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}
